export type CoinReceiptSource = '做题' | '修改答案' | '加题';

export interface CoinReceipt {
  topicId: number;
  topicTitle: string;
  source: CoinReceiptSource;
  coinsAdded: number;
  reason?: string;
}

export interface CoinReceiptEntry extends CoinReceipt {
  at: number;
}

export interface CoinReceiptGroup {
  topicId: number;
  topicTitle: string;
  entries: CoinReceiptEntry[];
}

const STORAGE_KEY = 'j-table-coin-receipt';
/** 只按题目留最近的若干道，多出来的整题换掉，不按日期清理。 */
export const COIN_RECEIPT_TOPIC_LIMIT = 15;

function readStoredEntries(): CoinReceiptEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as CoinReceiptEntry[] | { entries?: CoinReceiptEntry[] };
    const entries = Array.isArray(parsed) ? parsed : parsed?.entries;
    return Array.isArray(entries) ? entries : [];
  } catch {
    return [];
  }
}

function keepLatestTopics(entries: CoinReceiptEntry[]): CoinReceiptEntry[] {
  const latestByTopic = new Map<number, number>();
  entries.forEach((entry) => {
    const latest = latestByTopic.get(entry.topicId) ?? 0;
    if (entry.at >= latest) {
      latestByTopic.set(entry.topicId, entry.at);
    }
  });
  const ranked: Array<[number, number]> = [];
  latestByTopic.forEach((at, topicId) => {
    ranked.push([topicId, at]);
  });
  ranked.sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  const kept = new Set(ranked.slice(0, COIN_RECEIPT_TOPIC_LIMIT).map(([topicId]) => topicId));
  return entries.filter((entry) => kept.has(entry.topicId));
}

export function readCoinReceipts(): CoinReceiptEntry[] {
  return keepLatestTopics(readStoredEntries());
}

export function appendCoinReceipt(receipt?: CoinReceipt | null) {
  if (!receipt || receipt.topicId == null || Number.isNaN(Number(receipt.topicId))) {
    return;
  }
  const entry: CoinReceiptEntry = {
    topicId: Number(receipt.topicId),
    topicTitle: receipt.topicTitle || '',
    source: receipt.source,
    coinsAdded: Number(receipt.coinsAdded) || 0,
    at: Date.now(),
    ...(receipt.reason ? { reason: receipt.reason } : {}),
  };
  const entries = keepLatestTopics([...readStoredEntries(), entry]);
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries }));
}

export function sumCoinReceipts(entries: Array<{ coinsAdded: number }>) {
  return entries.reduce((sum, entry) => sum + (Number(entry.coinsAdded) || 0), 0);
}

export function formatCoinReceiptLine(entry: Pick<CoinReceipt, 'source' | 'coinsAdded' | 'reason'>) {
  const label = entry.source ? `${entry.source} ` : '';
  const line = `${label}+${entry.coinsAdded}`;
  return entry.reason ? `${line}，${entry.reason}` : line;
}

/** 同一道题收成一块，最近有入账的题目排在前面。 */
export function groupCoinReceiptsByTopic(entries: CoinReceiptEntry[]): CoinReceiptGroup[] {
  const map = new Map<number, CoinReceiptGroup & { latest: number }>();
  for (const entry of entries) {
    const current = map.get(entry.topicId);
    if (!current) {
      map.set(entry.topicId, {
        topicId: entry.topicId,
        topicTitle: entry.topicTitle,
        entries: [entry],
        latest: entry.at,
      });
      continue;
    }
    current.entries.push(entry);
    if (entry.topicTitle) {
      current.topicTitle = entry.topicTitle;
    }
    current.latest = Math.max(current.latest, entry.at);
  }
  return Array.from(map.values())
    .sort((a, b) => b.latest - a.latest)
    .map(({ topicId, topicTitle, entries: groupEntries }) => ({
      topicId,
      topicTitle,
      entries: groupEntries,
    }));
}
